/**
 * @fileoverview Onboarding mode — 2 opções de configuração no fluxo
 * "sem conta" (CTA C da WelcomeScreen): código do provedor ou
 * configuração manual (lista direta).
 *
 * Layout TV-first: cards horizontais com SVG, navegação ←/→.
 * Foco inicial em Provider Code (mais comum).
 *
 * Settings: NÃO aparece como card aqui — atalho discreto já existe
 * na Welcome (decisão Fernando 2026-05-09 — duplicação removida).
 *
 * BACK: volta pra Welcome (mesmo se `welcome-seen=true` — usuário
 * pediu pra rever opções, faz sentido voltar pra Welcome). Welcome
 * em si é tela raiz; user pode optar por outro fluxo (Criar conta /
 * Já tenho conta) ou apertar BACK de novo (confirma exit).
 *
 * FIX 2026-07 (LOOP SEM SAÍDA — o buraco que fez o dono desinstalar o app):
 * com SESSÃO SWIPE e SEM fonte, o RootRoute (caso 3) manda pra cá. O `handleBack`
 * navegava pra `/` e o RootRoute mandava de volta pra cá: BACK infinito, sem
 * nenhuma outra saída na tela. `clearWelcomeSeen()` não ajuda, porque o caso 3 é
 * avaliado ANTES do welcome-seen. Agora, quando existe sessão Swipe, o BACK e o
 * botão de saída abrem "Sair da conta" — encerra a sessão Swipe (o estado que
 * prende) e devolve o usuário ao gate/Welcome, sem loop.
 */

import { useTranslation } from '@app-iptv/i18n'
import { Button, Focusable, setFocus } from '@app-iptv/ui'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ConfirmDialog } from '../components/ConfirmDialog'
import { IconKey, IconLink } from '../components/PlayerIcons'
import { useBackKey } from '../hooks/useBackKey'
import { useFocusActivation } from '../hooks/useFocusActivation'
import { signOut } from '../sign-out'
import { hasSwipeSession } from '../sync'
import { clearWelcomeSeen } from '../welcome-flag'

import styles from './OnboardingModeScreen.module.css'

export function OnboardingModeScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const focusActive = useFocusActivation()
  const [confirmSignOut, setConfirmSignOut] = useState(false)

  // Preso pela sessão Swipe? (é ela que faz o RootRoute devolver pra cá.)
  // Lido no render: hasSwipeSession() é síncrono e estável durante a tela.
  const swipeBound = hasSwipeSession()

  useEffect(() => {
    setFocus('mode-provider-code')
  }, [])

  // Saída da tela. SEM sessão Swipe: volta pra Welcome (limpa welcome-seen pra o
  // RootRoute não redirecionar de volta pra cá). COM sessão Swipe: voltar pra `/`
  // é LOOP — a única saída honesta é encerrar a conta, então pedimos confirmação.
  const handleBack = useCallback(() => {
    if (swipeBound) {
      setConfirmSignOut(true)
      return
    }
    clearWelcomeSeen()
    navigate('/')
  }, [navigate, swipeBound])

  // Sair da conta: encerra TUDO (sync, token local + revogação no servidor,
  // credencial Xtream lembrada e dado pessoal do aparelho) ANTES de navegar — o
  // RootRoute lê `hasSwipeSession()` no render, então só depois de limpo ele
  // deixa de mandar pra cá. Fechadura única: `signOut()` (sign-out.ts), a mesma
  // das outras 3 portas de saída. O flush do pendente acontece lá dentro, com
  // teto de tempo: rede ruim não pode prender o usuário na conta.
  const doSignOut = useCallback(async () => {
    setConfirmSignOut(false)
    await signOut()
    navigate('/', { replace: true })
  }, [navigate])

  useBackKey(() => {
    if (confirmSignOut) {
      setConfirmSignOut(false)
      return
    }
    handleBack()
  })

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t('onboardingMode.title')}</h1>
        <p className={styles.subtitle}>{t('onboardingMode.subtitle')}</p>
      </header>

      <div
        className={`${styles.cards} ${focusActive ? styles.focusActive : styles.focusCold}`}
      >
        <Focusable
          focusKey="mode-provider-code"
          onEnterPress={() => navigate('/code')}
          onArrowPress={(dir) => {
            if (dir === 'right') {
              setFocus('mode-direct-login')
              return false
            }
            if (dir === 'down') {
              setFocus('mode-back')
              return false
            }
            return true
          }}
          className={styles.card}
        >
          <span className={styles.cardIcon} aria-hidden="true">
            <IconKey size={36} />
          </span>
          <h2 className={styles.cardTitle}>
            {t('onboardingMode.providerCode.title')}
          </h2>
          <p className={styles.cardDesc}>
            {t('onboardingMode.providerCode.desc')}
          </p>
        </Focusable>

        <Focusable
          focusKey="mode-direct-login"
          // `/source/manual` (CONTA) e não mais `/direct` (DEVICE): quem chega
          // aqui já tem sessão Swipe — é o caso 3 do RootRoute —, então a fonte
          // deve ficar amarrada à CONTA e sincronizar entre aparelhos. O
          // `/direct` continua existindo pro caminho de device, mas saiu do
          // seletor. (Decisão do dono, 14/08.)
          onEnterPress={() => navigate('/source/manual')}
          onArrowPress={(dir) => {
            if (dir === 'left') {
              setFocus('mode-provider-code')
              return false
            }
            if (dir === 'down') {
              setFocus('mode-back')
              return false
            }
            return true
          }}
          className={styles.card}
        >
          <span className={styles.cardIcon} aria-hidden="true">
            <IconLink size={36} />
          </span>
          <h2 className={styles.cardTitle}>
            {t('onboardingMode.directLogin.title')}
          </h2>
          <p className={styles.cardDesc}>
            {t('onboardingMode.directLogin.desc')}
          </p>
        </Focusable>
      </div>

      {/* Saída SEMPRE visível e alcançável por D-pad (nunca um beco sem saída).
          Sem sessão Swipe: "Voltar" (pra Welcome). Com sessão Swipe: "Sair da
          conta" — voltar seria loop (RootRoute caso 3 devolve pra cá), então a
          copy diz o que o botão FAZ de verdade.
          ↑ explicit pra Norigin LRUD strict (Tizen 2017): sem isso,
          foco poderia prender. */}
      <Button
        focusKey="mode-back"
        variant="ghost"
        onPress={handleBack}
        onArrowPress={(dir) => {
          if (dir === 'up') {
            // ↑ volta pro 1º card (foco inicial canônico da tela).
            // Mais previsível que hardcoded no card do meio.
            setFocus('mode-provider-code')
            return false
          }
          return true
        }}
      >
        {swipeBound ? t('gate.signOut') : t('common.back')}
      </Button>

      {confirmSignOut && (
        <ConfirmDialog
          title={t('gate.signOutTitle')}
          message={t('gate.signOutBody')}
          variant="danger"
          confirmLabel={t('gate.signOut')}
          onConfirm={() => void doSignOut()}
          onCancel={() => setConfirmSignOut(false)}
        />
      )}
    </div>
  )
}
